// OrderStepper.tsx
import React, { useMemo } from "react";
import { 
  CheckCircle, 
  CreditCard, 
  CheckSquare, 
  Calendar, 
  Tool, 
  Check, 
  XCircle, 
  Lock, 
  Slash 
} from "feather-icons-react";

type OrderType = "installation" | "removal";
type PaymentStatus =
  | "Pending"
  | "Paid"
  | "Partial Payment"
  | "Awaiting Payment"
  | "Overpaid";

type Step = {
  status: number;
  label: string;
  icon?: React.ComponentType<any>;
  kind?: "payment" | "normal" | "cancel" | "void" | "complete";
};

const PAYMENT = 150;

// Installation statuses
const INSTALL = {
  Draft: 100,
  Finalized: 200,
  Scheduled: 300,
  InProgress: 400,
  Completed: 500,
  Cancelled: 600,
} as const;

// Removal statuses
const REMOVAL = {
  Draft: 100,
  Finalized: 200,
  Scheduled: 300,
  InProgress: 400,
  Completed: 500,
  Closed: 600,
  Cancelled: 700,
  Void: 800,
} as const;

function paymentVariant(s?: PaymentStatus | null) {
  if (!s) return "neutral";
  if (s === "Paid" || s === "Overpaid") return "success";
  if (s === "Partial Payment") return "warning";
  return "neutral"; // Pending / Awaiting
}

function buildSteps(type: OrderType): { steps: Step[]; cancelEnds: number[]; voidEnds: number[] } {
  if (type === "installation") {
    return {
      steps: [
        { status: INSTALL.Draft, label: "Draft", icon: CheckCircle, kind: "complete" },
        { status: PAYMENT, label: "Payment", icon: CreditCard, kind: "payment" },
        { status: INSTALL.Finalized, label: "Finalized", icon: CheckSquare, kind: "complete" },
        { status: INSTALL.Scheduled, label: "Scheduled", icon: Calendar },
        { status: INSTALL.InProgress, label: "In Progress", icon: Tool },
        { status: INSTALL.Completed, label: "Completed", icon: Check, kind: "complete" },
        { status: INSTALL.Cancelled, label: "Cancelled", icon: XCircle, kind: "cancel" },
      ],
      cancelEnds: [INSTALL.Cancelled],
      voidEnds: [],
    };
  }
  return {
    steps: [
      { status: REMOVAL.Draft, label: "Draft", icon: CheckCircle, kind: "complete" },
      { status: PAYMENT, label: "Payment", icon: CreditCard, kind: "payment" },
      { status: REMOVAL.Finalized, label: "Finalized", icon: CheckSquare, kind: "complete" },
      { status: REMOVAL.Scheduled, label: "Scheduled", icon: Calendar },
      { status: REMOVAL.InProgress, label: "In Progress", icon: Tool },
      { status: REMOVAL.Completed, label: "Completed", icon: Check, kind: "complete" },
      { status: REMOVAL.Closed, label: "Closed", icon: Lock },
      { status: REMOVAL.Cancelled, label: "Cancelled", icon: XCircle, kind: "cancel" },
      { status: REMOVAL.Void, label: "Void", icon: Slash, kind: "void" },
    ],
    cancelEnds: [REMOVAL.Cancelled],
    voidEnds: [REMOVAL.Void],
  };
}

function applyDraftLabel(steps: Step[], draftLabel: string): Step[] {
  return steps.map((s) => (s.status === 100 ? { ...s, label: draftLabel } : s));
}

export default function InstallationStatusTracker({
  type,
  status,
  paymentStatus,
  draftLabel = "Draft",
  className = "",
  onStepClick,
}: {
  type: OrderType;
  status: number;
  paymentStatus?: PaymentStatus | null;
  /** Label for status 100 (always Draft). */
  draftLabel?: string;
  /** Kept so existing callers still compile; ignored. */
  quoteModeEnabled?: boolean;
  /** Kept so existing callers still compile; ignored. */
  ownerNodeId?: string;
  className?: string;
  onStepClick?: (s: Step) => void;
}) {
  const effectiveDraftLabel = draftLabel || "Draft";

  const { steps, cancelEnds, voidEnds } = useMemo(() => {
    const built = buildSteps(type);
    return {
      ...built,
      steps: applyDraftLabel(built.steps, effectiveDraftLabel),
    };
  }, [type, effectiveDraftLabel]);
  
  // Find the current active step index based on the passed status
  const activeIdx = steps.findIndex(s => s.status === status);
  const isCancelTerminal = cancelEnds.includes(status);
  const isVoidTerminal = voidEnds.includes(status);

  // Helper function to determine step state
  const getStepState = (step: Step, index: number) => {
    // Special case: status 0 means "adding state" - all steps are pending
    if (status === 0) {
      return { isActive: false, isDone: false, isNext: false, variant: "" };
    }
    
    // A step is active if it matches the current status
    const isActive = step.status === status;
    
    // A step is done if it's a step that comes before the current status (and we're not in a terminal state)
    const isDone = (step.status < status && !isCancelTerminal && !isVoidTerminal);
    
    // Find the next step: first step with status greater than current status (excluding terminal states)
    const isNext = !isCancelTerminal && !isVoidTerminal && 
                   step.status > status && 
                   step.kind !== "cancel" && 
                   step.kind !== "void" &&
                   steps.filter(s => s.status > status && s.kind !== "cancel" && s.kind !== "void")[0]?.status === step.status;
    
    let variant = "";
    if (step.kind === "payment") {
      variant = `is-pay-${paymentVariant(paymentStatus)}`;
    } else if (step.kind === "cancel") {
      variant = "is-cancel";
    } else if (step.kind === "void") {
      variant = "is-void";
    } else if (step.kind === "complete") {
      variant = "is-complete";
    }

    return { isActive, isDone, isNext, variant };
  };

  return (
    <div className={`mb-2 px-4 px-lg-0 ${className}`}>
      {/* Mobile Layout - 2-Column Grid (Default View for mobile/tablet) */}
      <div className="d-block d-lg-none">
        <div className="row pt-2">
          {steps.map((s, i) => {
            const { isActive, isDone, isNext, variant } = getStepState(s, i);
            
            // Always use consistent gray background, only change icon and text colors
            let iconClass = "text-secondary";
            let textClass = "text-secondary";
            
            if (isDone || isActive) {
              // Current status and all previous statuses should be green (completed)
              iconClass = "text-success";
              textClass = "text-success";
            } else if (isNext) {
              // Next immediate status should be blue (upcoming)
              iconClass = "text-primary";
              textClass = "text-primary";
            }

            return (
              <div key={s.status} className="col-6 ps-0">
                <div
                  className={`p-2 rounded-3 ${variant} cursor-pointer shadow-sm mobile-status-item`}
                  onClick={() => onStepClick?.(s)}
                  style={{ 
                    transition: 'all 0.2s ease',
                    minHeight: '50px', /* Reduced from 60px to 50px */
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <div className={`rounded-circle bg-white d-flex align-items-center justify-content-center mobile-status-icon`} style={{ width: '24px', height: '24px', flexShrink: 0 }}>
                      {s.icon && <s.icon size={14} className={iconClass} />}
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className={`fw-medium small text-truncate ${textClass}`}>
                        {s.kind === "payment" && paymentStatus ? paymentStatus : s.label}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop Layout - Horizontal Progress Bar (Only on large screens) */}
      <div className="d-none d-lg-block">
        <ol className="os-line m-0 p-0" style={{ ["--steps" as any]: steps.length }}>
          {steps.map((s, i) => {
            const { isActive, isDone, isNext, variant } = getStepState(s, i);

            return (
              <li
                key={s.status}
                className={[
                  "os-step",
                  i === 0 && "is-first",
                  (isActive || isDone) && "is-done", // Both current and previous should show as done (green)
                  isNext && "is-next", // Next step should show as next (blue)
                  variant,
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-current={isActive ? "step" : undefined}
                title={s.label}
                onClick={() => onStepClick?.(s)}
              >
                <span className="os-node">
                  {s.icon && <s.icon size={20} />}
                </span>
                <span className="os-label">
                  {s.kind === "payment" && paymentStatus ? paymentStatus : s.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

export { INSTALL as InstallStatus, REMOVAL as RemovalStatus, PAYMENT as PaymentStep };